// components/patient/FeedbackSubmissionForm.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  updateSubmission,
  submitFeedback,
  addAttachedFile,
  removeAttachedFile,
  setVoiceRecording,
  clearSubmissionError,
  FeedbackTag,
  UrgencyLevel,
  VisitLocation,
  VisitType,
  FeedbackSubmissionData,
} from '../../store/patientFeedbackSlice';
import VoiceRecorder from './VoiceRecorder';
import FileUpload from './FileUpload';
import SmartTextArea from './SmartTextArea';
import { useHospitalConfig } from '../../hooks/useHospitalConfig';
import {
  ExclamationCircleIcon,
  MicrophoneIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { useDraftAutosave } from '../../hooks/useDraftAutosave';

interface FeedbackSubmissionFormProps {
  onSubmissionComplete?: (trackingNumber: string, feedbackId: number) => void;
}

const TAG_OPTIONS: { id: FeedbackTag; label: string; description: string }[] = [
  { id: 'nursing', label: 'Nursing Care', description: 'Nursing staff, bedside care, shift coverage' },
  { id: 'medical', label: 'Medical / Physician', description: 'Doctors, diagnosis, treatment decisions' },
  { id: 'financial', label: 'Financial & Billing', description: 'Billing questions, payments, insurance' },
  { id: 'facilities', label: 'Facilities & Utilities', description: 'Rooms, cleanliness, equipment, amenities' },
  { id: 'safety', label: 'Safety & Security', description: 'Safety concerns, emergencies, security' },
  { id: 'service', label: 'Customer Service', description: 'Front desk, scheduling, communication' },
  { id: 'other', label: 'Other', description: 'Anything else you would like us to review' },
];

const URGENCY_OPTIONS: { id: UrgencyLevel; label: string; description: string }[] = [
  { id: 'standard', label: 'Standard', description: 'General feedback. We aim to respond within 1 business day.' },
  { id: 'urgent', label: 'Urgent', description: 'Needs attention soon (e.g., today).' },
  { id: 'emergency', label: 'Emergency', description: 'Requires immediate attention. We will escalate right away.' },
];

const VISIT_TYPE_OPTIONS: { id: VisitType; label: string; description: string }[] = [
  { id: 'emergency', label: 'Emergency Department', description: 'Emergency Room / Trauma care visit' },
  { id: 'inpatient', label: 'Inpatient Stay', description: 'Stayed overnight in a hospital unit or ward' },
  { id: 'outpatient', label: 'Outpatient / Clinic', description: 'Clinic, procedure, or same-day visit' },
  { id: 'virtual', label: 'Virtual / No Visit', description: 'Telehealth or not related to a hospital visit' },
];

const VISIT_LOCATION_OPTIONS: Record<Exclude<VisitType, ''>, { id: VisitLocation; label: string }[]> = {
  emergency: [
    { id: 'er', label: 'Emergency Room' },
    { id: 'other', label: 'Other emergency location' },
  ],
  inpatient: [
    { id: 'ward', label: 'Inpatient Ward' },
    { id: 'other', label: 'Other inpatient area' },
  ],
  outpatient: [
    { id: 'opd', label: 'Outpatient Clinic' },
    { id: 'other', label: 'Other outpatient area' },
  ],
  virtual: [
    { id: 'other', label: 'Virtual / Remote' },
  ],
};

const FeedbackSubmissionForm: React.FC<FeedbackSubmissionFormProps> = ({ onSubmissionComplete }) => {
  const dispatch = useAppDispatch();
  const { config } = useHospitalConfig();
  const {
    currentSubmission,
    isSubmitting,
    submissionError,
    attachedFiles,
    voiceRecording,
  } = useAppSelector((state) => state.patientFeedback);

  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const { initialValue: draftContent, saveDraft: saveDraftContent, clearDraft: clearDraftContent } = useDraftAutosave({
    draftKey: 'feedback_content',
  });

  const initialData = useMemo(() => ({
    content: currentSubmission?.content || draftContent || '',
    contact_name: currentSubmission?.contact_name || '',
    contact_phone: currentSubmission?.contact_phone || '',
    contact_email: currentSubmission?.contact_email || '',
    room_number: currentSubmission?.room_number || '',
    is_anonymous: currentSubmission?.is_anonymous ?? true,
    consent_given: currentSubmission?.consent_given ?? false,
    is_visit_related: currentSubmission?.is_visit_related ?? true,
    visit_type: (currentSubmission?.visit_type || '') as VisitType,
    visit_location: (currentSubmission?.visit_location || '') as VisitLocation,
    urgency_level: (currentSubmission?.urgency_level || 'standard') as UrgencyLevel,
    feedback_tags: (currentSubmission?.feedback_tags as FeedbackTag[] | undefined) || [],
    hospital_code: config?.hospital_code,
  }), [currentSubmission, config?.hospital_code, draftContent]);

  const [localFormData, setLocalFormData] = useState(initialData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setLocalFormData(initialData);
  }, [initialData]);

  useEffect(() => {
    if (submissionError) {
      dispatch(clearSubmissionError());
    }
  }, [localFormData.content, submissionError, dispatch]);

  const updateField = (field: keyof typeof localFormData, value: any) => {
    setLocalFormData((prev) => {
      const next = {
        ...prev,
        [field]: value,
      };
      dispatch(updateSubmission(next));
      return next;
    });

    // Auto-save content draft
    if (field === 'content') {
      saveDraftContent(value as string);
    }

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const toggleTag = (tag: FeedbackTag) => {
    setLocalFormData((prev) => {
      const exists = prev.feedback_tags.includes(tag);
      const feedback_tags = exists
        ? prev.feedback_tags.filter((item) => item !== tag)
        : [...prev.feedback_tags, tag];
      const next = { ...prev, feedback_tags };
      dispatch(updateSubmission(next));
      return next;
    });
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!localFormData.content.trim()) {
      newErrors.content = 'Please describe your feedback or concern.';
    } else if (localFormData.content.trim().length < 10) {
      newErrors.content = 'Please provide more detail (at least 10 characters).';
    }

    if (!localFormData.consent_given) {
      newErrors.consent = 'Please confirm you understand how we will use your information.';
    }

    if (localFormData.is_visit_related) {
      if (!localFormData.visit_type) {
        newErrors.visit_type = 'Please select where the visit occurred.';
      } else if (
        localFormData.visit_type !== 'virtual' &&
        !localFormData.visit_location
      ) {
        newErrors.visit_location = 'Please choose the specific location for your visit.';
      }
    }

    if (!localFormData.is_anonymous) {
      if (!localFormData.contact_name.trim()) {
        newErrors.contact_name = 'Name is required so we can reach you.';
      }
      if (!localFormData.contact_phone.trim() && !localFormData.contact_email.trim()) {
        newErrors.contact_phone = 'Please provide a phone number or email address.';
      }
      if (
        localFormData.contact_email &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(localFormData.contact_email)
      ) {
        newErrors.contact_email = 'Please enter a valid email address.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmission = async () => {
    if (!validateForm()) {
      return;
    }

    const payload: FeedbackSubmissionData = {
      content: localFormData.content.trim(),
      feedback_tags: localFormData.feedback_tags,
      feedback_category: (localFormData.feedback_tags[0] as FeedbackTag | undefined) || 'general',
      submission_method: (voiceRecording
        ? 'voice_recording'
        : attachedFiles.length > 0
        ? 'file_upload'
        : 'web_form') as FeedbackSubmissionData['submission_method'],
      original_language: 'en',
      contact_name: localFormData.is_anonymous ? undefined : localFormData.contact_name.trim() || undefined,
      contact_phone: localFormData.is_anonymous ? undefined : localFormData.contact_phone.trim() || undefined,
      contact_email: localFormData.is_anonymous ? undefined : localFormData.contact_email.trim() || undefined,
      room_number: localFormData.room_number.trim() || undefined,
      is_anonymous: localFormData.is_anonymous,
      consent_given: localFormData.consent_given,
      is_visit_related: localFormData.is_visit_related,
      visit_type: localFormData.is_visit_related ? localFormData.visit_type || undefined : undefined,
      visit_location:
        localFormData.is_visit_related && localFormData.visit_type !== 'virtual'
          ? localFormData.visit_location || undefined
          : undefined,
      urgency_level: localFormData.urgency_level,
      hospital_code: localFormData.hospital_code || config?.hospital_code,
    };

    try {
      const result = await dispatch(submitFeedback(payload)).unwrap();
      clearDraftContent();
      if (onSubmissionComplete) {
        onSubmissionComplete(result.tracking_number, result.id);
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    }
  };

  const handleVoiceRecordingComplete = (audioBlob: Blob, transcript: string) => {
    dispatch(setVoiceRecording(audioBlob));
    const currentContent = localFormData.content.trim();
    const newContent = currentContent
      ? `${currentContent}\n\n[Voice Recording]: ${transcript}`
      : `[Voice Recording]: ${transcript}`;
    updateField('content', newContent);
    setShowVoiceRecorder(false);
  };

  return (
    <div className="space-y-10">
      <section className="space-y-6">
        <h2 className="text-xl font-semibold text-gray-900">Visit Information</h2>
        <p className="text-gray-700 text-sm">
          Let us know if this feedback is tied to a recent visit so we can connect you with the right team.
        </p>
        <div className="flex flex-wrap gap-3">
          {[true, false].map((value) => (
            <button
              key={value ? 'visit-yes' : 'visit-no'}
              type="button"
              onClick={() => {
                updateField('is_visit_related', value);
                if (!value) {
                  updateField('visit_type', '' as VisitType);
                  updateField('visit_location', '' as VisitLocation);
                }
              }}
              className={`px-4 py-2 rounded-lg border transition-colors ${
                localFormData.is_visit_related === value
                  ? 'border-blue-600 bg-blue-50 text-blue-700'
                  : 'border-gray-300 hover:border-blue-400'
              }`}
            >
              {value ? 'It is related to a visit' : 'Not related to a visit'}
            </button>
          ))}
        </div>
        {errors.visit_type && (
          <p className="text-sm text-red-600 flex items-center">
            <ExclamationCircleIcon className="h-4 w-4 mr-1" />
            {errors.visit_type}
          </p>
        )}
        {localFormData.is_visit_related && (
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-3">
              {VISIT_TYPE_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => {
                    updateField('visit_type', option.id);
                    if (option.id === 'virtual') {
                      updateField('visit_location', 'other');
                    } else {
                      updateField('visit_location', localFormData.visit_type === option.id ? localFormData.visit_location : '');
                    }
                  }}
                  className={`text-left p-4 border rounded-lg transition ${
                    localFormData.visit_type === option.id
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-400'
                  }`}
                >
                  <p className="font-medium text-gray-900">{option.label}</p>
                  <p className="text-sm text-gray-600 mt-1">{option.description}</p>
                </button>
              ))}
            </div>
            {localFormData.visit_type && (
              <div>
                <h3 className="text-sm font-medium text-gray-800 mb-2">Where did it happen?</h3>
                <div className="flex flex-wrap gap-2">
                  {VISIT_LOCATION_OPTIONS[localFormData.visit_type]?.map((location) => (
                    <button
                      key={location.id}
                      type="button"
                      onClick={() => updateField('visit_location', location.id)}
                      className={`px-3 py-2 rounded-lg border text-sm transition ${
                        localFormData.visit_location === location.id
                          ? 'border-blue-600 bg-blue-50 text-blue-700'
                          : 'border-gray-300 hover:border-blue-400'
                      }`}
                    >
                      {location.label}
                    </button>
                  ))}
                </div>
                {errors.visit_location && (
                  <p className="text-sm text-red-600 mt-1 flex items-center">
                    <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                    {errors.visit_location}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </section>

      <section className="space-y-6">
        <h2 className="text-xl font-semibold text-gray-900">Help us route your feedback</h2>
        <p className="text-gray-700 text-sm">
          Select all topics that apply so we can involve the right specialists.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          {TAG_OPTIONS.map((tag) => {
            const selected = localFormData.feedback_tags.includes(tag.id);
            return (
              <button
                key={tag.id}
                type="button"
                onClick={() => toggleTag(tag.id)}
                className={`text-left p-4 border rounded-lg transition ${
                  selected ? 'border-green-600 bg-green-50' : 'border-gray-200 hover:border-green-400'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900">{tag.label}</span>
                  {selected && <SparklesIcon className="h-5 w-5 text-green-600" />}
                </div>
                <p className="text-sm text-gray-600 mt-2">{tag.description}</p>
              </button>
            );
          })}
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-gray-900">How urgent is this?</h2>
        <div className="grid md:grid-cols-3 gap-3">
          {URGENCY_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => updateField('urgency_level', option.id)}
              className={`text-left p-4 border rounded-lg transition ${
                localFormData.urgency_level === option.id
                  ? 'border-red-500 bg-red-50'
                  : 'border-gray-200 hover:border-red-400'
              }`}
            >
              <p className="font-medium text-gray-900">{option.label}</p>
              <p className="text-sm text-gray-600 mt-1">{option.description}</p>
            </button>
          ))}
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-gray-900">Tell us more</h2>
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">
            Describe your experience *
          </label>
          <button
            type="button"
            onClick={() => setShowVoiceRecorder(true)}
            className="flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <MicrophoneIcon className="h-4 w-4 mr-1" />
            Add Voice Note
          </button>
        </div>

        <SmartTextArea
          ref={textAreaRef}
          value={localFormData.content}
          onChange={(value) => updateField('content', value)}
          placeholder="Please share as many details as you can. Names, times, and specific examples help us respond quickly."
          error={errors.content}
          minLength={10}
          maxLength={2000}
          showCharacterCount
          aiSuggestions
        />

        {showVoiceRecorder && (
          <VoiceRecorder
            onRecordingComplete={handleVoiceRecordingComplete}
            onCancel={() => setShowVoiceRecorder(false)}
            existingContent={localFormData.content}
          />
        )}

        <FileUpload
          onFileSelect={(file) => dispatch(addAttachedFile(file))}
          onFileRemove={(index) => dispatch(removeAttachedFile(index))}
          attachedFiles={attachedFiles}
          maxFiles={5}
          maxSizePerFile={10 * 1024 * 1024}
          acceptedTypes={['image/*', '.pdf', '.doc', '.docx', '.txt']}
        />
      </section>

      <section className="space-y-6">
        <h2 className="text-xl font-semibold text-gray-900">Contact preferences</h2>
        <div className="bg-gray-50 p-4 rounded-lg">
          <label className="flex items-start space-x-3">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              checked={localFormData.is_anonymous}
              onChange={(e) => updateField('is_anonymous', e.target.checked)}
            />
            <span className="text-sm text-gray-700">
              Submit anonymously (we can still investigate, but we cannot reach you directly with updates).
            </span>
          </label>
        </div>

        {!localFormData.is_anonymous && (
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Your Name *</label>
              <input
                type="text"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                value={localFormData.contact_name}
                onChange={(e) => updateField('contact_name', e.target.value)}
              />
              {errors.contact_name && <p className="text-sm text-red-600 mt-1">{errors.contact_name}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Phone</label>
              <input
                type="tel"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                value={localFormData.contact_phone}
                onChange={(e) => updateField('contact_phone', e.target.value)}
              />
              {errors.contact_phone && <p className="text-sm text-red-600 mt-1">{errors.contact_phone}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                value={localFormData.contact_email}
                onChange={(e) => updateField('contact_email', e.target.value)}
              />
              {errors.contact_email && <p className="text-sm text-red-600 mt-1">{errors.contact_email}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Room Number (optional)</label>
              <input
                type="text"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                value={localFormData.room_number}
                onChange={(e) => updateField('room_number', e.target.value)}
              />
            </div>
          </div>
        )}
      </section>

      <section className="space-y-4">
        <div className="bg-blue-50 border border-blue-100 p-4 rounded-lg">
          <label className="flex items-start space-x-3">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              checked={localFormData.consent_given}
              onChange={(e) => updateField('consent_given', e.target.checked)}
            />
            <span className="text-sm text-blue-900">
              I understand that my feedback will be reviewed by the hospital staff. Sensitive information will be handled according to our privacy policy.
            </span>
          </label>
          {errors.consent && (
            <p className="text-sm text-red-600 mt-2 flex items-center">
              <ExclamationCircleIcon className="h-4 w-4 mr-1" />
              {errors.consent}
            </p>
          )}
        </div>
      </section>

      {submissionError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {submissionError}
        </div>
      )}

      <div className="flex items-center justify-end space-x-3">
        <button
          type="button"
          onClick={handleSubmission}
          className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting…' : 'Submit Feedback'}
        </button>
      </div>
    </div>
  );
};

export default FeedbackSubmissionForm;